/**
 * Client mirror of PUT /api/employees/:id authorization (server/routes/employees.js),
 * so the UI never offers an employee edit the server refuses.
 *
 * - employees:update_all (HR/admin): any employee, every field (on their own record the
 *   server keeps only its self-edit list; the UI hides Edit on the own row for everyone).
 * - employees:update_department (department_manager): someone else in the caller's own
 *   department, judged on users.department_id (AuthContext `user.department_id`), and only
 *   DEPARTMENT_MANAGER_EDITABLE_FIELDS. A null department on either side never matches.
 * - employees:update_own admits no write on that route, so AuthContext.hasUpdatePermission()
 *   (update_all OR update_own) must not gate employee edits.
 *
 * PUT /:id/payment-methods and /:id/slack-link stay update_all only: see canEditAnyEmployeeField.
 * Each helper takes AuthContext's `hasPermission(resource, action)`.
 */

/**
 * The only keys a department manager may send. Mirrors
 * EmployeeService.getAllowedEmployeeUpdateFieldsForDepartmentManager() on the server,
 * which refuses a body naming any other key with 403 FIELDS_NOT_EDITABLE.
 */
export const DEPARTMENT_MANAGER_EDITABLE_FIELDS = Object.freeze([
  'position', 'phone', 'address', 'city', 'country',
  'emergency_contact_name', 'emergency_contact_phone', 'emergency_contact_relationship',
]);

/** Every field, payment methods and documents included: update_all only. */
export const canEditAnyEmployeeField = (hasPermission) => hasPermission('employees', 'update_all');

/** Whether the caller can edit some employee, before a record is known. */
export const canEditEmployees = (hasPermission) =>
  canEditAnyEmployeeField(hasPermission) || hasPermission('employees', 'update_department');

/**
 * Whether the caller can edit this employee. The UI additionally hides the Edit
 * affordance on the viewer's own row for everyone; that stays at the call sites.
 *
 * @param {Function} hasPermission AuthContext's hasPermission.
 * @param {{ employee_id?: number|null, department_id?: number|null }|null} user The signed-in user.
 * @param {{ id: number|string, department_id?: number|string|null }|null} employee The record.
 * @returns {boolean}
 */
export const canEditEmployee = (hasPermission, user, employee) => {
  if (canEditAnyEmployeeField(hasPermission)) return true;
  if (!hasPermission('employees', 'update_department') || !employee) return false;
  if (user?.employee_id != null && String(employee.id) === String(user.employee_id)) return false;
  return user?.department_id != null && employee.department_id != null
    && String(user.department_id) === String(employee.department_id);
};

/**
 * Toast copy for a failed PUT /api/employees/:id. FIELDS_NOT_EDITABLE carries the
 * refused keys (e.g. `department_id`); they are named in plain words.
 */
export const employeeUpdateErrorMessage = (err) => {
  const data = err?.response?.data;
  if (data?.code === 'FIELDS_NOT_EDITABLE' && Array.isArray(data.fields) && data.fields.length > 0) {
    const names = data.fields.map((field) => field.replace(/_id$/, '').replace(/_/g, ' '));
    return `Only HR can change: ${names.join(', ')}.`;
  }
  return data?.message || 'Failed to update employee';
};
